import { Box, Chip, Collapse, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { foyerApi, type DeviceExitEvent } from "../http/foyer";
import { useFormat } from "../i18n/format";
import { translateKey } from "../i18n/translate";

interface Props {
  premiseId: string;
  screenId: string;
  /** From the heartbeat: a shell that cannot report says so instead of "none". */
  platform?: string;
}

const REASON_KEY: Record<string, string> = {
  ANR: "anr",
  "CRASH (java)": "crashJava",
  "CRASH (native)": "crashNative",
  LOW_MEMORY: "lowMemory",
  RENDERER_CRASH: "rendererCrash",
  RENDERER_KILLED: "rendererKilled",
  EXIT_SELF: "exitSelf",
  "USER_REQUESTED (force-stop)": "forceStop",
};

/**
 * Crashes, freezes and restarts the TV reported by itself on boot (kiosk
 * 0.2.49+). Before that, these were only visible inside a "Capture logs"
 * file, so a box that crashed overnight looked fine the next morning.
 */
export default function DeviceExitsPanel({
  premiseId,
  screenId,
  platform,
}: Props) {
  const { t } = useTranslation("premises");
  const fmt = useFormat();
  const [events, setEvents] = useState<DeviceExitEvent[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    setEvents(null);
    setFailed(false);
    foyerApi
      .screenExitEvents(premiseId, screenId, 14)
      .then((r) => live && setEvents(r.events))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [premiseId, screenId]);

  if (failed) {
    return (
      <Typography variant="caption" color="error.main">
        {t("deviceExitsPanel.loadFailed")}
      </Typography>
    );
  }
  if (!events) return null;
  if (events.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {platform && platform !== "stb" && platform !== "philips"
          ? t("deviceExitsPanel.notReported")
          : t("deviceExitsPanel.empty")}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} data-testid="device-exits">
      {events.map((e, i) => (
        <Box key={`${e.at}-${i}`}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={() => e.trace && setOpen(open === i ? null : i)}
            sx={{ cursor: e.trace ? "pointer" : "default" }}
          >
            <Chip
              size="small"
              color={e.fault ? "error" : "default"}
              variant={e.fault ? "filled" : "outlined"}
              label={
                REASON_KEY[e.reason]
                  ? translateKey(
                      t,
                      `premises:deviceExitsPanel.reasons.${REASON_KEY[e.reason]}`
                    )
                  : e.reason
              }
            />
            <Typography variant="body2">
              {fmt.dateTime(e.at, { dateStyle: "short", timeStyle: "medium" })}
            </Typography>
            {e.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {e.description}
              </Typography>
            )}
            {e.trace && (
              <Typography variant="caption" color="primary.main">
                {open === i
                  ? t("deviceExitsPanel.hideTrace")
                  : t("deviceExitsPanel.trace")}
              </Typography>
            )}
          </Stack>
          {e.trace && (
            <Collapse in={open === i} unmountOnExit>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1,
                  typography: "micro",
                  maxHeight: 240,
                  overflow: "auto",
                  bgcolor: "action.hover",
                  borderRadius: 1,
                }}
              >
                {e.trace}
              </Box>
            </Collapse>
          )}
        </Box>
      ))}
    </Stack>
  );
}
